import {
  Document,
  ImageRun,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';

import logo from '../../../../assets/wm_intellio_advantage_logo.jpg';
import { ProjectConfig, ProjectSalesforceInfo } from '../../../../Types/dealGPT';
import { AnalysisQuestionStatus } from '../../../../Types/enums';
import { DocxReport } from '../../../../util/DocxReport';
import { KPIStatusColors } from '../../utils';

export type ProjectOverviewDocxReportProps = {
  projectName: string;
  config: ProjectConfig;
  salesforce: ProjectSalesforceInfo;
};

export class ProjectOverviewDocxReport extends DocxReport<ProjectOverviewDocxReportProps> {
  protected logo: ArrayBuffer | null = null;
  constructor(reportData: ProjectOverviewDocxReportProps) {
    super();
    this.data = reportData;
  }

  protected async fetchData(): Promise<void> {
    const response = await fetch(logo);
    if (response.ok) {
      this.logo = await response.arrayBuffer();
    }
  }

  protected createDocument(): Document {
    if (!this.data) throw new Error('Project Data was not loaded');

    const logoParagraph = this.logo
      ? new Paragraph({
          children: [
            new ImageRun({
              data: this.logo,
              transformation: {
                width: 150,
                height: 50,
              },
              type: 'jpg',
            }),
          ],
          alignment: 'left',
          spacing: { after: 300 },
        })
      : new Paragraph({});

    const dateParagraph = new Paragraph({
      children: [
        new TextRun({
          text: new Date().toLocaleDateString(),
          font: 'Arial',
          bold: true,
          size: '12pt',
        }),
      ],
      alignment: 'left',
      spacing: { after: 300 },
    });

    return new Document({
      sections: [
        {
          children: [
            logoParagraph,
            dateParagraph,
            this.createTitle(this.data.projectName),
            ...this.createSalesforceSection(),
            ...this.createKPIsSection(),
            ...this.createKeyQuestionsSection(),
          ],
        },
      ],
    });
  }

  private createTitle(title: string): Paragraph {
    return new Paragraph({
      children: [new TextRun({ text: title, font: 'Arial', bold: true, size: '20pt' })],
      spacing: { after: 400 },
    });
  }

  private createSalesforceSection(): Paragraph[] {
    const salesforce = this.data?.salesforce;

    const fields: Paragraph[] = [];

    if (salesforce?.PEFirm) {
      fields.push(this.createField('Account', salesforce.PEFirm));
    }

    if (salesforce?.industry) {
      fields.push(this.createField('Industry', salesforce.industry));
    }

    if (salesforce?.subIndustry) {
      fields.push(this.createField('Sub-Industry', salesforce.subIndustry));
    }

    return fields;
  }

  private createKPIsSection(): Array<Paragraph | Table> {
    const kpiRows = (this.data?.config.kpiQuestions ?? []).map(
      (kpi) =>
        new TableRow({
          children: [
            new TableCell({
              width: { size: 33, type: WidthType.PERCENTAGE },
              margins: { top: 100, bottom: 100, left: 100, right: 100 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: kpi.prettyQuestion ?? '',
                      font: 'Arial',
                      bold: true,
                      size: '12pt',
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 33, type: WidthType.PERCENTAGE },
              margins: { top: 100, bottom: 100, left: 100, right: 100 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: kpi.value ?? 'N/A', font: 'Arial', size: '12pt' }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 34, type: WidthType.PERCENTAGE },
              margins: { top: 100, bottom: 100, left: 100, right: 100 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${this.getStatusLabel(kpi.status, kpi.value)} ${this.getStatusIcon(
                        kpi.status,
                        kpi.value
                      )}`,
                      font: 'Arial',
                      size: '12pt',
                      color:
                        KPIStatusColors[kpi.status] ??
                        KPIStatusColors[
                          kpi.value
                            ? AnalysisQuestionStatus.NEEDS_CONFIRMATION
                            : AnalysisQuestionStatus.NOT_FOUND
                        ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
    );

    return [
      new Paragraph({
        children: [new TextRun({ text: 'Basic Facts', font: 'Arial', bold: true, size: '18pt' })],
        spacing: { after: 300 },
      }),
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                width: { size: 33, type: WidthType.PERCENTAGE },
                margins: { top: 100, bottom: 100, left: 100, right: 100 }, // Add padding
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'KPI', font: 'Arial', bold: true, size: '14pt' }),
                    ],
                  }),
                ],
              }),
              new TableCell({
                width: { size: 33, type: WidthType.PERCENTAGE },
                margins: { top: 100, bottom: 100, left: 100, right: 100 }, // Add padding
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Answer', font: 'Arial', bold: true, size: '14pt' }),
                    ],
                  }),
                ],
              }),
              new TableCell({
                width: { size: 34, type: WidthType.PERCENTAGE },
                margins: { top: 100, bottom: 100, left: 100, right: 100 }, // Add padding
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Status', font: 'Arial', bold: true, size: '14pt' }),
                    ],
                  }),
                ],
              }),
            ],
          }),
          ...kpiRows,
        ],
      }),
      new Paragraph({ spacing: { after: 300 } }),
    ];
  }

  private getStatusLabel(status: AnalysisQuestionStatus, value: string | null | undefined): string {
    switch (status) {
      case AnalysisQuestionStatus.ANSWERED:
        return value ? 'Needs Confirmation' : 'Not Found'; // If status is answered, but value is empty, show 'Not Found'
      case AnalysisQuestionStatus.CONFIRMED:
        return 'Confirmed Answer';
      case AnalysisQuestionStatus.NEEDS_CONFIRMATION:
        return 'Needs Confirmation';
      case AnalysisQuestionStatus.NOT_FOUND:
        return 'Not Found';
      default:
        return '';
    }
  }

  private getStatusIcon(status: AnalysisQuestionStatus, value: string | null | undefined): string {
    switch (status) {
      case AnalysisQuestionStatus.ANSWERED:
        return value ? '⚠️' : '❌'; // If status is answered, but value is empty, show '❌'
      case AnalysisQuestionStatus.CONFIRMED:
        return '✅';
      case AnalysisQuestionStatus.NEEDS_CONFIRMATION:
        return '⚠️';
      case AnalysisQuestionStatus.NOT_FOUND:
        return '❌';
      default:
        return '';
    }
  }

  private createKeyQuestionsSection(): Paragraph[] {
    return [
      new Paragraph({
        children: [new TextRun({ text: 'Key Questions', font: 'Arial', bold: true, size: '18pt' })],
        spacing: { after: 300 },
      }),
      ...(this.data?.config.keyQuestions ?? []).map(
        (q) =>
          new Paragraph({
            children: [new TextRun({ text: q.question, font: 'Arial', size: '14pt' })],
            bullet: { level: 0 },
            spacing: { after: 150 },
          })
      ),
    ];
  }

  private createField(label: string, value?: string): Paragraph {
    return new Paragraph({
      children: [
        new TextRun({ text: `${label}: `, font: 'Arial', bold: true, size: '12pt' }),
        new TextRun({ text: value ?? '', font: 'Arial', size: '12pt' }),
      ],
      spacing: { after: 150 },
    });
  }
}
